// «Реклама» → блок «Кабинеты»: здоровье рекламных кабинетов Meta — статус и причина, лимит расходов кабинета
// и на сколько дней его хватит, оплата, доля отклонённых объявлений за 7 дней, пояс и валюта (подсвечены, если не
// Ташкент и не доллар). Проверяет worker раз в 30 минут (apps/worker/src/meta/accounts.ts), считает api
// (/api/ads/accounts). Поля, которые токену не видны, показаны как «не видно», а не нулём.
import { Button, Card, Chip, int, Meter, money, pct, Skeleton, when } from '@autoaff/ui';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { TriangleAlert } from 'lucide-react';
import type { ReactNode } from 'react';
import { api } from '../../api';
import './breakdowns.css';

export interface AccountHealthView {
  id: string;
  name: string;
  status: number;
  statusText: string;
  ok: boolean;
  reason: string | null;
  currency: string;
  timezone: string | null;
  tzMismatch: boolean;
  currencyMismatch: boolean;
  spendCap: number | null;
  amountSpent: number | null;
  capLeft: number | null;
  daysLeft: number | null;
  pace: number | null;
  payment: {
    problem: boolean;
    balance: number | null;
    prepay: boolean | null;
    method: string | null;
    methodKnown: boolean;
    hasMethod: boolean | null;
  };
  deliveryChecks: string[];
  disapproved: {
    reviewed: number;
    disapproved: number;
    share: number | null;
    prevShare: number | null;
    growing: boolean;
  };
  withIssues: number;
  unavailable: string[];
  checkedAt: string | null;
}

/** Лимит кончится раньше — подсветка (как тревога worker). */
const CAP_WARN_DAYS = 3;

/** Статус кабинета коротко — для чипа; полный текст — в подсказке. */
const STATUS_SHORT: Record<number, string> = {
  1: 'Активен',
  2: 'Отключён',
  3: 'Долг',
  7: 'Проверка Meta',
  8: 'Ждёт расчёта',
  9: 'Льготный период',
  100: 'Закрывается',
  101: 'Закрыт',
};

const daysText = (n: number) => (n < 1 ? 'меньше дня' : `~${n.toFixed(1)} дн.`);

function Cell({
  label,
  children,
  sub,
  tone,
}: {
  label: string;
  children: ReactNode;
  sub?: ReactNode;
  tone?: string;
}) {
  return (
    <div className={clsx('cab-cell', tone)}>
      <span className="cab-label">{label}</span>
      <b className="tnum">{children}</b>
      {sub && <div className="cab-sub">{sub}</div>}
    </div>
  );
}

function Row({ a }: { a: AccountHealthView }) {
  const capTone =
    a.capLeft !== null && a.capLeft <= 0
      ? 'danger'
      : a.daysLeft !== null && a.daysLeft < CAP_WARN_DAYS
        ? 'warn'
        : undefined;
  const d = a.disapproved;
  const methodText =
    a.payment.method ??
    (a.payment.hasMethod
      ? 'способ оплаты задан'
      : a.payment.methodKnown
        ? 'способ оплаты не найден'
        : 'способ оплаты токену не виден');
  return (
    <li className="cab-row">
      <div className="cab-name">
        <b title={a.name}>{a.name}</b>
        <span title={capFirst(a.statusText)}>
          <Chip tone={a.ok ? 'success' : 'danger'} dot>
            {STATUS_SHORT[a.status] ?? capFirst(a.statusText)}
          </Chip>
        </span>
        {a.reason && <small className="danger">Причина: {a.reason}</small>}
      </div>
      <Cell
        label="Осталось до лимита расходов"
        tone={capTone}
        sub={
          a.spendCap === null ? (
            'Meta не остановит рекламу по сумме'
          ) : (
            <>
              <Meter
                value={a.amountSpent !== null && a.spendCap > 0 ? a.amountSpent / a.spendCap : 0}
                tone={capTone === 'danger' ? 'danger' : capTone === 'warn' ? 'warn' : undefined}
                label="Потрачено из лимита"
              />
              {a.capLeft !== null && a.capLeft <= 0
                ? 'исчерпан — реклама стоит'
                : a.daysLeft !== null
                  ? `${daysText(a.daysLeft)} при ${money(a.pace ?? 0, a.currency)} в день`
                  : 'расхода сейчас нет'}
            </>
          )
        }
      >
        {a.spendCap === null
          ? 'без лимита'
          : `${money(a.capLeft ?? 0, a.currency)} из ${money(a.spendCap, a.currency)}`}
      </Cell>
      <Cell
        label="Отклонено за 7 дней"
        tone={d.growing ? 'warn' : undefined}
        sub={
          <>
            {d.prevShare !== null ? `неделей раньше ${pct(d.prevShare)}` : 'неделей раньше не было новых'}
            {a.withIssues > 0 && ` · с замечаниями ${int(a.withIssues)}`}
          </>
        }
      >
        {d.reviewed > 0
          ? `${int(d.disapproved)} из ${int(d.reviewed)} · ${pct(d.share ?? 0)}`
          : 'новых не было'}
      </Cell>
      <Cell
        label="Оплата"
        tone={a.payment.problem ? 'danger' : undefined}
        sub={
          <span
            title={
              a.unavailable.length
                ? `Токену не видны поля: ${a.unavailable.join(', ')} — нужна задача «Управление» на кабинет`
                : undefined
            }
          >
            {a.payment.prepay === null ? '' : a.payment.prepay ? 'предоплата · ' : 'постоплата · '}
            {methodText}
          </span>
        }
      >
        {a.payment.problem
          ? 'есть проблема'
          : a.payment.balance === null
            ? 'к оплате: не видно'
            : a.payment.balance > 0
              ? `к оплате ${money(a.payment.balance, a.currency)}`
              : 'долга нет'}
      </Cell>
      <Cell
        label="Пояс и валюта"
        tone={a.tzMismatch || a.currencyMismatch ? 'warn' : undefined}
        sub={
          a.tzMismatch
            ? 'не Ташкент: дни Meta сдвинуты от наших'
            : a.currencyMismatch
              ? 'не доллар: расход в валюте кабинета'
              : 'как у платформы'
        }
      >
        <span className={clsx(a.tzMismatch && 'cab-flag')}>{a.timezone ?? 'пояс не известен'}</span>
        {' · '}
        <span className={clsx(a.currencyMismatch && 'cab-flag')}>{a.currency}</span>
      </Cell>
      {a.deliveryChecks.length > 0 && (
        <ul className="cab-checks">
          {a.deliveryChecks.map((c) => (
            <li key={c}>
              <TriangleAlert aria-hidden />
              <span>Meta: {c}</span>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

export function AdsAccounts() {
  const q = useQuery({
    queryKey: ['ads-accounts'],
    queryFn: () => api<{ accounts: AccountHealthView[] }>('/api/ads/accounts'),
    refetchInterval: 5 * 60_000,
  });
  const list = q.data?.accounts;
  if (q.isError && !list)
    return (
      <Card title="Кабинеты" className="cab-card">
        <p className="muted cab-error">
          Не удалось загрузить кабинеты.{' '}
          <Button size="sm" variant="ghost" onClick={() => q.refetch()}>
            Повторить
          </Button>
        </p>
      </Card>
    );
  if (!list)
    return (
      <Card title="Кабинеты" className="cab-card">
        <Skeleton h={48} />
      </Card>
    );
  if (!list.length) return null;
  const checked = list
    .map((a) => a.checkedAt)
    .filter((x): x is string => x !== null)
    .sort()[0];
  return (
    <Card
      title="Кабинеты"
      count={list.length > 1 ? list.length : undefined}
      className="cab-card"
      action={
        <span className="muted cab-when">
          {checked ? `проверено ${when(checked)}` : 'ещё не проверялись'}
        </span>
      }
    >
      <ul className="cab-list">
        {list.map((a) => (
          <Row key={a.id} a={a} />
        ))}
      </ul>
    </Card>
  );
}

const capFirst = (s: string) => `${s.charAt(0).toUpperCase()}${s.slice(1)}`;
